import { useLocation } from 'react-router-dom';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '@/lib/supabase';
import { PREBUILT_TOOL_KEYS } from '@shared/types/prebuilt-tools';
import { getPrebuiltToolManifest } from '@shared/types/tool-manifest';
import { sweepBusinessVocabulary } from '@/lib/ui-editorial-sweeper';
import type { AppSummary } from '@/lib/app-access';
import type { ToolAppKind, ToolAppStatus, ToolAppVisibility } from '@shared/types/tool-apps';

export interface WorkspaceApplication {
  id: string;
  title: string;
  description: string;
  href: string;
  kind: 'application' | 'tool' | 'standard';
  /** The app behind a tool or read surfaces, when the user may manage it. */
  managed?: AppSummary;
}
interface CatalogContextValue {
  /** Published apps and standard tools. */
  applications: WorkspaceApplication[];
  /** Drafts and archives the user manages (creator or manager). */
  unpublished: WorkspaceApplication[];
  pinnedIds: string[];
  loading: boolean;
  error: string | null;
  preferenceError: string | null;
  refresh: () => Promise<void>;
  togglePin: (id: string) => void;
}
const CatalogContext = createContext<CatalogContextValue | null>(null);
const standards: WorkspaceApplication[] = PREBUILT_TOOL_KEYS.map((key) => {
  const manifest = getPrebuiltToolManifest(key);
  return {
    id: `standard:${key}`,
    title: manifest.product.title,
    description: manifest.product.promise,
    href: `/tools/${key}`,
    kind: 'standard',
  };
});

export const ApplicationCatalogProvider = ({ children }: { children: ReactNode }) => {
  const [applications, setApplications] = useState<WorkspaceApplication[]>([]);
  const [unpublished, setUnpublished] = useState<WorkspaceApplication[]>([]);
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [storageKey, setStorageKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Votre session doit être renouvelée.');
      // F5-0: every app, read surfaces or Forge tool (its flow). RLS shows drafts
      // and archives only to their creator and the managers.
      const [apps, member] = await Promise.all([
        supabase.from('tool_apps').select('id, key, title, description, kind, visibility, status, owner_user_id, flows(id)')
          .order('title'),
        supabase.from('tenant_members').select('tenant_id, tenant_role').eq('user_id', user.id).maybeSingle(),
      ]);
      if (apps.error || member.error) {
        throw new Error('Impossible de charger les applications. Réessayez.');
      }
      const isManager = member.data?.tenant_role === 'manager';
      const all: Array<WorkspaceApplication & { status: ToolAppStatus }> = (apps.data ?? []).map((app) => {
        const flowId = app.kind === 'flow' ? (app.flows as Array<{ id: string }> | null)?.[0]?.id : undefined;
        const title = sweepBusinessVocabulary(app.title as string);
        const canManage = isManager || app.owner_user_id === user.id;
        return {
          // A tool keeps its former id so its pins survive the move to apps.
          id: flowId ? `tool:${flowId}` : `app:${app.key}`,
          title,
          description: sweepBusinessVocabulary((app.description as string | null) ?? ''),
          href: `/apps/${encodeURIComponent(app.key as string)}`,
          kind: flowId ? 'tool' as const : 'application' as const,
          status: app.status as ToolAppStatus,
          managed: canManage ? {
            id: app.id as string, key: app.key as string, title, kind: app.kind as ToolAppKind,
            visibility: app.visibility as ToolAppVisibility, status: app.status as ToolAppStatus,
            ownerUserId: app.owner_user_id as string, flowId: flowId ?? null,
          } : undefined,
        };
      });
      const list: WorkspaceApplication[] = [...all.filter((app) => app.status === 'published'), ...standards];
      setApplications(list);
      setUnpublished(all.filter((app) => app.status !== 'published' && app.managed));
      const key = `domino.applications.pins:${member.data?.tenant_id ?? 'personal'}:${user.id}`;
      setStorageKey(key);
      try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]');
        setPinnedIds(
          Array.isArray(stored)
            ? stored.filter((id): id is string =>
              typeof id === 'string' && list.some((app) => app.id === id)
            )
            : [],
        );
      } catch {
        setPinnedIds([]);
        setPreferenceError('Les raccourcis de ce navigateur n’ont pas pu être retrouvés.');
      }
    } catch (cause) {
      setApplications([]);
      setUnpublished([]);
      setPinnedIds([]);
      setError(cause instanceof Error ? cause.message : 'Impossible de charger les applications.');
    } finally {
      setLoading(false);
    }
  }, []);
  const { pathname } = useLocation();
  const didLoad = useRef(false);
  useEffect(() => {
    if (!didLoad.current || pathname === '/tools') {
      didLoad.current = true;
      void refresh();
    }
  }, [pathname, refresh]);
  const togglePin = useCallback((id: string) => {
    if (!storageKey || !applications.some((app) => app.id === id)) return;
    const next = pinnedIds.includes(id)
      ? pinnedIds.filter((item) => item !== id)
      : [...pinnedIds, id];
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      setPinnedIds(next);
      setPreferenceError(null);
    } catch {
      setPreferenceError('Ce navigateur ne permet pas d’enregistrer vos raccourcis.');
    }
  }, [applications, pinnedIds, storageKey]);
  const value = useMemo(
    () => ({ applications, unpublished, pinnedIds, loading, error, preferenceError, refresh, togglePin }),
    [applications, unpublished, pinnedIds, loading, error, preferenceError, refresh, togglePin],
  );
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};
export const useApplicationCatalog = () => {
  const context = useContext(CatalogContext);
  if (!context) throw new Error('ApplicationCatalogProvider is required.');
  return context;
};
